import React, { FC, useMemo } from 'react';

import LoadingSpinner from '../../components/nav/LoadingSpinner';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import useFormulasCategoryCountQuery from '../../hooks/formulas/useFormulasCategoryCount';
import useMetricStatsQuery from '../../hooks/metrics/useMetricStatsQuery';

import { FormulaCategory } from '../../models/formula.model';
import { MonitoringChartTypes } from '../../models/monitoring.model';

const SummarySlide: FC<React.PropsWithChildren<unknown>> = () => {
    const { data, isPending: isLoading } = useMetricStatsQuery({
        statsType: MonitoringChartTypes.GLOBAL,
        fetchMetricCards: true,
    });
    const { data: formulasCount } = useFormulasCategoryCountQuery();

    const liveMessagesCount = useMemo<string>(() => {
        let count: number = 0;
        if (formulasCount) {
            const liveMessages = formulasCount[FormulaCategory.Live];
            count = Object.values(liveMessages).reduce(
                (acc, count) => acc + count,
                0,
            );
        }
        return count.toLocaleString();
    }, [formulasCount]);

    if (isLoading) {
        return <LoadingSpinner styles={{ height: 100 }} />;
    }

    return (
        <Container>
            <Block>
                <Title>Active users (Last 60 days)</Title>
                <Description>
                    {data?.stats.unique_active_users.toLocaleString() || 0}
                </Description>
            </Block>
            <Block>
                <Title>Active message groups</Title>
                <Description>{liveMessagesCount}</Description>
            </Block>
            <Block>
                <Title>Active messages</Title>
                <Description>
                    {data?.stats.active_messages.toLocaleString() || 0}
                </Description>
            </Block>
            <Block>
                <Title>Message scheduled today</Title>
                <Description>
                    {data?.stats.messages_scheduled_today.toLocaleString() || 0}
                </Description>
            </Block>
        </Container>
    );
};

const Container = styled.span(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(8),
}));

const Block = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
}));

const Title = styled.p({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
});

const Description = styled.p(() => ({
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.textLarge,
    fontWeight: 400,
}));

export default SummarySlide;
